import type { ColDef } from 'ag-grid-enterprise';
import { z } from 'zod';

const baseRowSchema = z.object({
  id: z.union([z.number(), z.string()]).transform(String),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  created_by: z.string().optional(),
  updated_by: z.string().optional(),
  ref_id: z.string().optional().nullable(),
});

export const expenseCategorySchema = baseRowSchema.extend({
  task_group: z.string().optional().nullable(),
  task: z.string().optional().nullable(),
  is_active: z.string().optional().nullable(),
});

export const projectCategorySchema = baseRowSchema.extend({
  project_category: z.string().optional().nullable(),
});

export const projectClassSchema = baseRowSchema.extend({
  class_type: z.string().optional().nullable(),
  class_range: z.string().optional().nullable(),
});

export const projectContractTypeSchema = baseRowSchema.extend({
  contract_type: z.string().optional().nullable(),
});

export const projectPhaseSchema = baseRowSchema.extend({
  phase: z.string().optional().nullable(),
  phase_name: z.string().optional().nullable(),
});

export const projectPortfolioSchema = baseRowSchema.extend({
  project_portfolio: z.string().optional().nullable(),
});

export type ProjectPropertyFieldType = 'text' | 'select';

export interface ProjectPropertyFieldDef {
  key: string;
  label: string;
  type: ProjectPropertyFieldType;
  options?: { value: string; label: string }[];
}

export interface ProjectPropertyTableConfig {
  key: string;
  title: string;
  description: string;
  bentoClass: string;
  apiSegment: string;
  queryKey: string;
  listEnvelopeKey: string;
  singleEnvelopeKey: string;
  fields: ProjectPropertyFieldDef[];
  gridColumns: ColDef[];
  rowSchema: z.ZodTypeAny;
}

export type ProjectPropertyRow = {
  id: string;
  ref_id?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
  [key: string]: string | null | undefined;
};

const refIdColumn: ColDef = { field: 'ref_id', headerName: 'ID', width: 80 };

export const PROJECT_PROPERTY_TABLES: ProjectPropertyTableConfig[] = [
  {
    key: 'expense-categories',
    title: 'Expense Categories',
    description: 'Task groups and expense task types',
    bentoClass: 'col-span-2 row-span-2 lg:col-span-2 lg:row-span-2',
    apiSegment: 'expense-categories',
    queryKey: 'project-properties-expense-categories',
    listEnvelopeKey: 'expense_categories',
    singleEnvelopeKey: 'expense_category',
    fields: [
      { key: 'ref_id', label: 'ID', type: 'text' },
      { key: 'task_group', label: 'Task group', type: 'text' },
      { key: 'task', label: 'Task', type: 'text' },
      {
        key: 'is_active',
        label: 'Active',
        type: 'select',
        options: [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ],
      },
    ],
    gridColumns: [
      refIdColumn,
      { field: 'task_group', headerName: 'Task group', flex: 1, minWidth: 100 },
      { field: 'task', headerName: 'Task', flex: 1, minWidth: 100 },
      { field: 'is_active', headerName: 'Active', flex: 1, minWidth: 100 },
    ],
    rowSchema: expenseCategorySchema,
  },
  {
    key: 'project-portfolios',
    title: 'Project Portfolios',
    description: 'Portfolio groupings',
    bentoClass: 'col-span-1 row-span-1',
    apiSegment: 'project-portfolios',
    queryKey: 'project-properties-project-portfolios',
    listEnvelopeKey: 'project_portfolios',
    singleEnvelopeKey: 'project_portfolio',
    fields: [
      { key: 'ref_id', label: 'ID', type: 'text' },
      { key: 'project_portfolio', label: 'Portfolio', type: 'text' },
    ],
    gridColumns: [
      refIdColumn,
      { field: 'project_portfolio', headerName: 'Portfolio', flex: 1, minWidth: 120 },
    ],
    rowSchema: projectPortfolioSchema,
  },
  {
    key: 'project-categories',
    title: 'Project Categories',
    description: 'Process technology categories',
    bentoClass: 'col-span-1 row-span-1',
    apiSegment: 'project-categories',
    queryKey: 'project-properties-project-categories',
    listEnvelopeKey: 'project_categories',
    singleEnvelopeKey: 'project_category',
    fields: [
      { key: 'ref_id', label: 'ID', type: 'text' },
      { key: 'project_category', label: 'Category', type: 'text' },
    ],
    gridColumns: [
      refIdColumn,
      { field: 'project_category', headerName: 'Category', flex: 1, minWidth: 120 },
    ],
    rowSchema: projectCategorySchema,
  },
  {
    key: 'project-class',
    title: 'Project Class',
    description: 'Class type and value ranges',
    bentoClass: 'col-span-1 row-span-1',
    apiSegment: 'project-class',
    queryKey: 'project-properties-project-class',
    listEnvelopeKey: 'project_classes',
    singleEnvelopeKey: 'project_class',
    fields: [
      { key: 'ref_id', label: 'ID', type: 'text' },
      { key: 'class_type', label: 'Class type', type: 'text' },
      { key: 'class_range', label: 'Class range', type: 'text' },
    ],
    gridColumns: [
      refIdColumn,
      { field: 'class_type', headerName: 'Type', width: 80 },
      { field: 'class_range', headerName: 'Range', flex: 1, minWidth: 80 },
    ],
    rowSchema: projectClassSchema,
  },
  {
    key: 'project-contract-types',
    title: 'Contract Types',
    description: 'Delivery / contract models',
    bentoClass: 'col-span-1 row-span-1',
    apiSegment: 'project-contract-types',
    queryKey: 'project-properties-project-contract-types',
    listEnvelopeKey: 'project_contract_types',
    singleEnvelopeKey: 'project_contract_type',
    fields: [
      { key: 'ref_id', label: 'ID', type: 'text' },
      { key: 'contract_type', label: 'Contract type', type: 'text' },
    ],
    gridColumns: [
      refIdColumn,
      { field: 'contract_type', headerName: 'Contract type', flex: 1, minWidth: 120 },
    ],
    rowSchema: projectContractTypeSchema,
  },
  {
    key: 'project-phases',
    title: 'Project Phases',
    description: 'Phase codes and names',
    bentoClass: 'col-span-1 row-span-1',
    apiSegment: 'project-phases',
    queryKey: 'project-properties-project-phases',
    listEnvelopeKey: 'project_phases',
    singleEnvelopeKey: 'project_phase',
    fields: [
      { key: 'ref_id', label: 'ID', type: 'text' },
      { key: 'phase', label: 'Phase code', type: 'text' },
      { key: 'phase_name', label: 'Phase name', type: 'text' },
    ],
    gridColumns: [
      refIdColumn,
      { field: 'phase', headerName: 'Code', width: 100 },
      { field: 'phase_name', headerName: 'Name', minWidth: 100 },
    ],
    rowSchema: projectPhaseSchema,
  },
];

export function getProjectPropertyTable(key: string): ProjectPropertyTableConfig | undefined {
  return PROJECT_PROPERTY_TABLES.find((t) => t.key === key);
}
